import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { slugify, slugifyWithFallback } from '../utils/slug';

const API = buildApiUrl();

const courseCache = new Map();
const unitCache = new Map();
const courseSlugIndex = new Map();
const unitSlugIndex = new Map();

const cacheUnit = (unit) => {
  if (!unit || !unit.id) {
    return;
  }

  unitCache.set(unit.id, unit);

  const unitSlug = slugifyWithFallback(unit.title, `unit-${unit.id}`);
  if (unitSlug) {
    unitSlugIndex.set(unitSlug, unit.id);
  }
};

const cacheUnitsFromCourse = (course) => {
  if (!course || !Array.isArray(course.subject_groups)) {
    return;
  }

  course.subject_groups.forEach((group) => {
    if (!Array.isArray(group.sections)) {
      return;
    }
    group.sections.forEach((section) => {
      if (!Array.isArray(section.units)) {
        return;
      }
      section.units.forEach(cacheUnit);
    });
  });
};

const cacheCourse = (course) => {
  if (!course || !course.id) {
    return;
  }

  courseCache.set(course.id, course);

  const courseSlug = slugifyWithFallback(course.name, `course-${course.id}`);
  if (courseSlug) {
    courseSlugIndex.set(courseSlug, course.id);
  }

  cacheUnitsFromCourse(course);
};

const cacheCourses = (courses = []) => {
  courses.forEach(cacheCourse);
};

axios.interceptors.request.use((config) => {
  const u = localStorage.getItem('user');
  if (u) {
    try {
      const parsed = JSON.parse(u);
      const token = parsed?.access || parsed?.token;
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // Silently ignore token parse errors in production
    }
  }
  return config;
});

const courseService = {
  getCourses: async (params = {}) => {
    try {
      const res = await axios.get(`${API}/courses/`, { params });
      const data = res.data;
      if (Array.isArray(data)) {
        cacheCourses(data);
      }
      return data;
    } catch (error) {
      throw error;
    }
  },

  getUnits: async (params = {}) => {
    try {
      const res = await axios.get(`${API}/courses/units/`, { params });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getUnit: async (id) => {
    try {
      const res = await axios.get(`${API}/courses/units/${id}/`);
      const data = res.data;
      if (data) {
        cacheUnit(data);
      }
      return data;
    } catch (error) {
      throw error;
    }
  },

  getInProgress: async () => {
    try {
      const res = await axios.get(`${API}/courses/units/in-progress/`, {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });

      if (res.status === 404) {
        return [];
      }

      return res.data;
    } catch (error) {
      throw error;
    }
  },

  enrollCourse: async (courseId) => {
    try {
      const res = await axios.post(`${API}/courses/${courseId}/enroll/`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  trackProgress: async (unitId, data) => {
    try {
      const res = await axios.post(`${API}/courses/units/${unitId}/progress/`, data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // New methods for advanced features
  getCourseDetails: async (courseId, params = {}) => {
    try {
      const cachedCourse = courseCache.get(courseId);
      if (cachedCourse && params?.include_sections && Array.isArray(cachedCourse.subject_groups)) {
        return cachedCourse;
      }
      const res = await axios.get(`${API}/courses/${courseId}/`, { params });
      const data = res.data;
      cacheCourse(data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  getCourseSyllabus: async (courseId) => {
    try {
      const res = await axios.get(`${API}/courses/${courseId}/syllabus/`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getCourseReviews: async (courseId) => {
    try {
      const res = await axios.get(`${API}/courses/${courseId}/reviews/`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getCachedCourse: (courseId) => courseCache.get(courseId) || null,
  getCachedUnit: (unitId) => unitCache.get(unitId) || null,
  resolveCourseIdBySlug: (slug) => courseSlugIndex.get(slugify(slug)) || null,
  resolveUnitIdBySlug: (slug) => unitSlugIndex.get(slugify(slug)) || null,
  cacheCourseSnapshot: cacheCourse,
};

export default courseService;
